// kinedb result rows -> plain objects.
//
// A document SELECT answers two columns: `_id`, and `document`, a JSON string
// with the projected fields. The CVR collections came from a mongodump, so the
// body holds MongoDB Extended JSON: `{"$date":{"$numberLong":"<ms>"}}`. This
// module revives each wrapper, so a date is an ISO string and a long is a
// number. The same code is in cvrweb src/kinedb/rows.js and in the agent
// lib/extjson.js.
//
// `$numberLong` becomes a JS number. That loses precision above 2^53, but each
// use here is a timestamp in milliseconds or an `enhedsNummer`, far below it.

/** `$date` -> ISO string, `$numberLong`/`$numberInt`/`$numberDouble` -> number, `$oid` -> string. */
export function reviveExtendedJson(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(reviveExtendedJson);

  const keys = Object.keys(value);
  if (keys.length === 1) {
    const inner = value[keys[0]];
    switch (keys[0]) {
      case '$date': {
        const ms = (inner !== null && typeof inner === 'object')
          ? Number(inner.$numberLong)
          : (typeof inner === 'number' ? inner : Date.parse(String(inner)));
        return Number.isNaN(ms) ? null : new Date(ms).toISOString();
      }
      case '$numberLong':
      case '$numberInt':
      case '$numberDouble':
        return Number(inner);
      case '$oid':
        return String(inner);
      default:
        break;
    }
  }

  const out = {};
  for (const key of keys) out[key] = reviveExtendedJson(value[key]);
  return out;
}

function parseDocument(value) {
  if (value === null || value === undefined) return {};
  if (typeof value === 'object') return reviveExtendedJson(value);
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' ? reviveExtendedJson(parsed) : {};
  } catch {
    // A body that is not JSON is a change on the server, not a data error in
    // one row. The row keeps its `_id`, so it does not disappear from the page.
    return {};
  }
}

/** A kinedb `rows` response -> plain objects. The row's own `_id` wins over a body `_id`. */
export function mapRows(response) {
  const columns = response?.columns;
  const data = response?.data;
  if (!Array.isArray(columns) || !Array.isArray(data)) return [];

  const docIndex = columns.indexOf('document');
  return data.map((row) => {
    const out = {};
    if (docIndex >= 0) Object.assign(out, parseDocument(row[docIndex]));
    for (let i = 0; i < columns.length; i++) {
      if (i !== docIndex) out[columns[i]] = row[i];
    }
    return out;
  });
}

/** The single value of a one-cell result, for example `SELECT COUNT(*)`. */
export function firstScalar(response) {
  const data = response?.data;
  if (!Array.isArray(data) || data.length === 0) return 0;
  const first = data[0];
  return Array.isArray(first) ? first[0] : first;
}
